<template>

  <div id="roleManage">
    <div class="dialog-content">
      <div v-if="action === 'edit'" class="context">To edit a user role permissions, you can select or deselect the specific role permissions below.</div>

      <div v-else-if="action === 'selected-unit'" class="context">
        To add an existing user to this unit, select a user and a role. You may customize the user's specific role by selecting or deselecting role permissions. You can also <span class="vo-link" @click="inviteNewUser">invite a new user</span> to this unit.
      </div>

      <div v-else class="context">To add a new user role, start by choose the role template that is necessary. You may customize the user's specific role by selecting or deselecting role permissions.</div>

      <div class="title-area">User / Role</div>
      <div class="section">
        
        <!-- USER NAME -->
        <v-select 
          v-if="action === 'selected-unit'" 
          ref="selectUser"
          prepend-icon="mdi-account-circle"
          label="User" 
          autocomplete
          item-text="fullName"
          item-value="_id"
          :loading="usersLoading"  
          :rules="rules.user"
          :search-input.sync="userSearch"
          :items="users"
          v-model="user">
        </v-select>
        <v-text-field v-else :label="user.fullName" prepend-icon="mdi-account-circle" disabled></v-text-field>

        <!-- ROLE -->
        <v-select 
          v-if="role" 
          ref="selectRole"
          :label="action === 'edit' ? role.title : 'Role Template'" 
          v-model="role.parent" 
          prepend-icon="mdi-clipboard-account" 
          :disabled="action === 'edit'" 
          item-text="title" 
          :rules="rules.role"
          :items="roleTemplates">
        </v-select>

      </div>

      <div class="title-area">Permissions</div>
      <div class="section" :class="{disabled: !role.parent}">

        <div class="subtitle" style="margin-bottom: 16px;">Access Level</div>
      
        <!-- ACCESS LEVEL -->
        <div class="access-level" v-if="!role.parent && action !== 'selected-unit'">Select a role template...</div>
        <div v-else>
          <div class="access-level" v-if="accessLevel >= 3">All Properties</div>
    
          <!-- EDITING OR SELECTED-UNIT MODES - This is just for displaying -->
          <template v-if="action === 'edit' || action === 'selected-unit'">

            <v-select v-if="role.property" :label="$store.getters.property(role.property).name" prepend-icon="mdi-domain" disabled></v-select>

            <v-select v-if="role.unit" :label="$store.getters.unit(role.unit).title" prepend-icon="mdi-home" disabled></v-select>

          </template>

          <!-- CREATING - This is interacted with -->
          <template v-else>

            <template v-if="role.parent && role.parent.requires">
            
              <v-select 
                v-if="role.parent.requires.property" 
                ref="selectProperty"
                label="Property" 
                prepend-icon="mdi-domain" 
                item-text="name"
                item-value="_id"
                :rules="rules.property"
                :items="properties"
                v-model="role.property"> 
              </v-select>
            
              <v-select 
                v-if="role.parent.requires.unit" 
                ref="selectUnit"
                prepend-icon="mdi-home"
                label="Unit" 
                autocomplete
                item-text="title"
                item-value="_id"
                :loading="unitsLoading" 
                :disabled="!role.property" 
                :rules="rules.unit"
                :search-input.sync="unitSearch"
                :items="units"
                v-model="role.unit"> 
              </v-select>
          
            </template>

          </template>
        </div>

        <!-- ROLE PERMISSIONS -->
        <vo-role-permissions ref="rolePermissions" :accessLevel="accessLevel"></vo-role-permissions>
      </div>

    </div>
    <div class="dialog-footer">
      <v-btn class="button" :disabled="lockout.on" flat color="info" @click="routeRemoveQueries(['dialog', 'dialogAction', 'dialogItem'])">Cancel</v-btn>
      <v-btn class="button" :loading="lockout.on" flat color="primary" @click="action === 'edit' ? save() : create()">{{ action === 'edit' ? 'Save' : 'Create' }}</v-btn>
    </div>
  </div>
</template>

<script>

  import _ from 'lodash'
  import permissionMixin from '../mixins/permissionMixin.js'

  export default {
    mixins: [permissionMixin],
    data () {
      return {
        
        title: '',
        user: null,
        role: null,
        action: false,

        units: [],
        unitsLoading: false,
        unitSearch: '',

        users: [],
        usersLoading: false,
        userSearch: '',
        
        rules: {
          role: [
            function (e) {
              return !e ? 'Please select a role.' : true;
            }
          ],
          property: [
            function (e) {
              return !e ? 'Please select a property.' : true;
            }
          ],
          unit: [
            function (e) {
              return !e ? 'Please select a unit.' : true;
            }
          ],
          user: [
            function (e) {
              return !e ? 'Please select a user.' : true;
            }
          ]
        }

      }
    },  
    
    props: {
      lockout: {
        
      },
      dialogComponent: {

      }
    },

    methods: {
      
      inviteNewUser () {
        this.routeRemoveQueries(['dialog', 'dialogAction']);

        setTimeout(() => {
          this.routeAddQueries({dialog: 'invite-create', dialogAction: 'selected-unit'});
        }, 300);
      },

      save () {
        if (this.lockout.on) return;
        this.lockout.on = true;

        var role = {...this.role};
        role.permissions = this.$refs.rolePermissions.value;

        this.$store.dispatch('updateUserRole', {
          user: this.user._id,
          company: this.$store.getters.company._id,
          role: role
        })
        .then(data => {
          this.lockout.on = false;
          this.routeRemoveQueries(['dialog', 'dialogItem', 'dialogAction']);
          Toast.show('Role updated.', 'success');
        })
        .catch(e => {
          console.log(e);
          Toast.showError(e);
          this.lockout.on = false;
        })

      },

      create () {
        if (this.lockout.on) return;
        this.lockout.on = true;

        var valid = this.validate();

        if (!valid) {
          Toast.show('Please correct the highlighted errors.', 'error');
          this.lockout.on = false;
          return;
        }

        var role = {
          parent: this.role.parent._id,
          title: this.role.parent.title,
          company: this.$store.getters.company._id,
          permissions: this.$refs.rolePermissions.value
        }

        if (this.role.parent.requires) {
          if (this.role.parent.requires.property) role.property = this.role.property;
          if (this.role.parent.requires.unit)     role.unit = this.role.unit;
        }

        // If selecting user, this.user will be the ID so just swap it from the store
        if (this.action === 'selected-unit') this.user = this.$store.getters.user(this.user);

        this.$store.dispatch('addUserRole', {
          company: this.$store.getters.company._id,
          user: this.user._id,
          role: role
        })
        .then(data => {

          // Selected-Unit - Roles on page will update
          if (this.action === 'selected-unit') {
            
            // Dispatch event to reload unit
            window.dispatch('unitUpdated', {
              _id: this.role.unit
            });
          }

          this.routeRemoveQueries(['dialog', 'dialogAction', 'dialogItem']);
          Toast.show('User role added.', 'success');
          this.lockout.on = false;
        })
        .catch(e => {
          this.lockout.on = false;
          console.log(e);          
          Toast.showError(e);
        })
      },

      validate () {
        this.$refs.selectRole.validate(true);
        var valid = (this.$refs.selectRole.valid);

        if (this.action === 'selected-unit') {
          this.$refs.selectUser.validate();
          if (!this.$refs.selectUser.valid) valid = false;
        }
        else if (this.role.parent && this.role.parent.requires) {
          if (this.role.parent.requires.property) {
            this.$refs.selectProperty.validate(true);
            if (!this.$refs.selectProperty.valid) valid = false;
          }
          if (this.role.parent.requires.unit) {
            this.$refs.selectUnit.validate(true);
            if (!this.$refs.selectUnit.valid) valid = false;
          }
        }

        return valid;
      }
      
    },

    computed: {

      accessLevel () {
        // Different checks depending on the context, checking what is required versus what is already there
        if (this.action === 'edit' || this.action === 'selected-unit') return this.role.unit ? 1 : this.role.property ? 2 : 3; 
        else return !this.role.parent ? 1 : (this.role.parent.requires ? (this.role.parent.requires.unit ? 1 : 2) : 3);
      },
            
      roleTemplates () {

        // Check which roles they have access to inviting
        if (this.hasPermissions(['role']) && this.action !== 'selected-unit') {
          return _.values(this.$store.getters.roleTemplates)
        }
        else {
          // Just get Unit based roles
          var roles = [];
          
          for (var key in this.$store.getters.roleTemplates) {
            if (this.$store.getters.roleTemplates[key].requires && this.$store.getters.roleTemplates[key].requires.unit) {
              roles.push(this.$store.getters.roleTemplates[key]);
            }
          };

          return roles;
        }
      }, 

      properties () {
        return _.values(this.$store.getters.properties);
      }
    },
    
    watch: {
      // Creating & Editing
      'role.permissions' () {

        // Wait until $nextTick to allow AccessLevel to update on RolePermissions
        this.$nextTick(() => {
          if (this.role.permissions) this.$refs.rolePermissions.init(this.role.permissions);
        });
      },

      // Creating Only

      // Initialize permissions based on role template
      'role.parent' () {
        if (this.role.parent && this.role.parent.permissions) {
          this.role.permissions = {...this.role.parent.permissions};
        }
      },

      // Property changed, load units
      'role.property' () {
        if (this.action === 'selected-unit') {
          this.unitsLoading = false;
          return;
        }
        if (!this.role.property || !(this.role.parent && this.role.parent.requires && this.role.parent.requires.property)) return;

        this.units = [];
        this.unitsLoading = true;
        if (this.role.unit) this.role.unit = null; // Different property, so unit won't exist

        // Load initial units
        this.$store.dispatch('loadUnits', {
          company: this.$store.getters.company._id,
          filters: {
            property: this.role.property
          }
        })
        .then(data => {
          this.unitsLoading = false;
          this.units = data;
        })
        .catch(e => {
          this.unitsLoading = false;
          console.log(e);
          Toast.showError(e);
        });
      
      },
      
      /// Units searched
      unitSearch: _.debounce(function () { 
        if (this.action === 'selected-unit') {
          this.unitsLoading = false;
          return;
        }
        if (!this.role.property) return;
        this.unitsLoading = true;
        
        this.$store.dispatch('loadUnits', {
          company: this.$store.getters.company._id,
          filters: {
            property: this.role.property,
            search: this.unitSearch
          }
        })
        .then(data => {
          this.units = data;
          this.unitsLoading = false;
        })
        .catch(e => {
          console.log(e);
          Toast.showError(e);
          this.unitsLoading = false;
        })

      }, 200),

      /// Units searched
      userSearch: _.debounce(function () {
        if (!this.role.property) return;
        this.usersLoading = true;

        this.$store.dispatch('loadUsers', {
          company: this.$store.getters.company._id,
          filters: {
            search: this.userSearch,
            sort: 'lastName',
          }
        })
        .then(data => {
          this.users = data;
          this.usersLoading = false;
        })
        .catch(e => {
          console.log(e);
          Toast.showError(e);
          this.usersLoading = false;
        })

      }, 200),

      roleTemplates () {
        if ('parent' in this.role && this.roleTemplates.length === 1) {
          this.role.parent = this.roleTemplates[0];
        }
      }
    },
    
    created () {
      if (this.$route.query.dialogAction) this.action = this.$route.query.dialogAction;

      if (!this.action || this.action !== 'selected-unit') {
        this.user = this.$store.getters.user(this.app.pages.joined.selected);
      }  
    
      if (this.action === 'edit') {
        this.role = this.$store.getters.role(this.$route.query.dialogItem);
      }
      else if (this.action === 'selected-unit') {
        
        this.role = {
          permissions : null,
          parent      : null,
          unit        : this.$store.getters.unit(this.app.pages.units.selected)._id,
          property    : this.$store.getters.property(this.$store.getters.unit(this.app.pages.units.selected).property)._id
        };
        this.units = [this.$store.getters.unit(this.app.pages.units.selected)]; 
    
        // Init Users Search
        this.users = [];
        this.usersLoading = true;

        // Load initial users
        this.$store.dispatch('loadUsers', {
          company: this.$store.getters.company._id,
          filters: {
            sort: 'lastName'            
          }
        })
        .then(data => {
          this.usersLoading = false;
          this.users = data;
        })
        .catch(e => {
          this.usersLoading = false;
          console.log(e);
          Toast.showError(e);
        });
      }

      else {
        this.role = {
          parent: null,
          unit: null,
          property: null,
          permissions: null
        }
      }

      this.title = this.action === 'edit' ? 'Update Role' : 'Add Role'; 
      this.$emit('title', this.title);
    }
  }

</script>

<style lang="scss">

  @import '../css/global.scss';

  #roleManage {
    position: relative;

    .vo-link {
      @include fontPrimary500;
    }

    .dialog-content {
      padding: 24px;
    }

    .title-area {
      @include fontPrimary500();
      @include title();

      padding: 0 0 8px;
      border-bottom: $borderBlack12;
    }

    .section {
      padding: 16px;
      box-sizing: border-box;

      &.disabled {
        opacity: .24;
        pointer-events: none;
      }

      .subtitle {
        @include fontPrimary500();
        @include title();
        font-size: 12px;
        margin: 16px 0 0;
      }

      .access-level {
        margin: 0 0 24px 16px;
        color: $black54;
      }

    }
  }

</style>


// WEBPACK FOOTER //
// src/components/RoleManage.vue